import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Product, ProductFormData } from '../models/Product';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private baseUrl = environment.microservices.product;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  /**
   * Lista todos os produtos
   */
  getProducts(): Observable<Product[]> {
    return this.http.get<any[]>(`${this.baseUrl}/list/products`).pipe(
      map(list => (list || []).map(dto => this.mapFromDto(dto))),
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao buscar produtos'); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Busca um produto por ID
   */
  getProduct(id: number | string): Observable<Product> {
    return this.http.get<any>(`${this.baseUrl}/view/products/${id}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao buscar produto'); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Cria um novo produto
   */
  createProduct(productData: ProductFormData): Observable<Product> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    const body = { ...productData, kmIncluded: 0 };
    return this.http.post<any>(`${this.baseUrl}/create/products`, body, { headers }).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao criar produto'); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Atualiza um produto existente
   */
  updateProduct(id: number | string, productData: ProductFormData): Observable<Product> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    const body = { ...productData, kmIncluded: 0 };
    return this.http.put<any>(`${this.baseUrl}/edit/products/${id}`, body, { headers }).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao atualizar produto'); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Remove um produto
   */
  deleteProduct(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/delete/products/${id}`).pipe(
      catchError(err => { 
        this.errorService.showErrorMessage('Erro ao excluir produto'); 
        return throwError(() => err); 
      })
    );
  }

  /**
   * Resposta de view/create/update pode vir como objeto raiz ou encapsulado (product, data, etc.).
   */
  private unwrapProductPayload(raw: any): any {
    if (!raw || typeof raw !== 'object') {
      return raw;
    }
    const nested =
      raw.product ??
      raw.Product ??
      raw.data ??
      raw.Data ??
      raw.item ??
      raw.Item ??
      raw.result ??
      raw.Result;
    return nested != null && typeof nested === 'object' ? nested : raw;
  }

  /** Primeira propriedade definida (ignora apenas undefined); permite null só onde o chamador tratar. */
  private pickDefined<T>(o: any, keys: string[]): T | undefined {
    if (!o) return undefined;
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(o, k) && o[k] !== undefined) {
        return o[k] as T;
      }
    }
    return undefined;
  }

  private pickString(o: any, keys: string[], fallback = ''): string {
    const v = this.pickDefined<string | number>(o, keys);
    if (v === undefined || v === null) return fallback;
    return String(v);
  }

  /** Números: ignora null/'' e tenta várias chaves (API PT/EN). */
  private pickNumber(o: any, keys: string[], fallback = 0): number {
    if (!o) return fallback;
    for (const k of keys) {
      const v = o[k];
      if (v === undefined || v === null || v === '') continue;
      const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
      if (Number.isFinite(n)) return n;
    }
    return fallback;
  }

  private pickBool(o: any, keys: string[], fallback = false): boolean {
    if (!o) return fallback;
    for (const k of keys) {
      if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
      const v = o[k];
      if (v === undefined || v === null) continue;
      return Boolean(v);
    }
    return fallback;
  }

  /**
   * Converte DTO da API para modelo interno
   */
  private mapFromDto(raw: any): Product {
    const dto = this.unwrapProductPayload(raw);

    return {
      id: this.pickNumber(
        dto,
        ['Id', 'id', 'ID', 'ProductId', 'productId', 'ProductID', 'productID'],
        0
      ),
      code: this.pickString(dto, ['Code', 'code']),
      name: this.pickString(dto, ['Name', 'name']),
      type: this.pickString(dto, ['Type', 'type']),
      billingCycle: this.pickString(dto, ['BillingCycle', 'billingCycle']),
      days: this.pickNumber(dto, ['Days', 'days'], 0),
      basePrice: this.pickNumber(dto, ['BasePrice', 'basePrice', 'PrecoBase', 'precoBase'], 0),
      description: this.pickString(dto, ['Description', 'description']),
      isActive: this.pickBool(dto, ['IsActive', 'isActive'], true),
      depositAmount: this.pickNumber(
        dto,
        [
          'DepositAmount',
          'depositAmount',
          'deposit_amount',
          'ValorCaucao',
          'valorCaucao',
          'ValorDaCaucao',
          'valorDaCaucao',
          'CaucaoValor',
          'caucaoValor',
          'ValorCaução',
          'SecurityDeposit',
          'securityDeposit'
        ],
        0
      ),
      includeInsurance: this.pickBool(dto, ['IncludeInsurance', 'includeInsurance'], false),
      includeMaintenance: this.pickBool(dto, ['IncludeMaintenance', 'includeMaintenance'], false),
      createdAt: this.pickDefined<string>(dto, ['CreatedAt', 'createdAt']),
      updatedAt: this.pickDefined<string>(dto, ['UpdatedAt', 'updatedAt'])
    };
  }
}
